import type {
  AutocompleteInputChangeReason,
  InputBaseComponentProps,
  SxProps,
  Theme,
} from "@mui/material";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DesktopDatePicker, DesktopDateTimePicker } from "@mui/x-date-pickers";
import { isValid, parseISO } from "date-fns";
import { MuiTelInput } from "mui-tel-input";
import { useMemo } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PhoneMask } from "../InputFormat";
import { PriceInput } from "../PriceInput";

type InputType =
  | "TextField"
  | "PhoneField"
  | "DateField"
  | "DateTimeField"
  | "SelectField"
  | "RadioField"
  | "TextAreaField"
  | "Autocomplete"
  | "AsyncAutocomplete"
  | "PriceField";
type VariantType = "standard" | "outlined" | "filled";
type TextInputType = "text" | "email" | "password" | "number";

type Option = {
  label?: string;
  name?: string;
  value: string | number | boolean;
};

type AsyncAutocompleteOption = { value: string | number; label: string };

type TGenericInput = {
  name: string;
  label: string;
  field: ControllerRenderProps<FieldValues, string>;
  type: InputType;
  isEditAllowed?: boolean;
  variant?: VariantType;
  inputType?: TextInputType;
  disablePast?: boolean;
  greaterThan?: string;
  options?: Option[] | AsyncAutocompleteOption[];
  error: any;
  inputProps?: InputBaseComponentProps;
  sx?: SxProps<Theme>;
  /** Only for AsyncAutocomplete */
  asyncAutocompleteProps?: {
    loading?: boolean;
    noOptionsText?: string;
    inputValue?: string;
    onInputChange?: (
      event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => void;
    getOptionValue?: (option: AsyncAutocompleteOption) => string | number;
    initialOption?: AsyncAutocompleteOption | null;
    /** Last selected option (for display when options list is empty after selection) */
    selectedOption?: AsyncAutocompleteOption | null;
    /** Called when user selects an option so parent can persist it for display */
    onOptionSelect?: (option: AsyncAutocompleteOption | null) => void;
  };
  /** Only for PriceField: min, max, step, suffix */
  priceFieldProps?: { min?: number; max?: number; step?: number; suffix?: string };
};

function GenericInput({
  name,
  field,
  label,
  type,
  isEditAllowed = true,
  variant = "standard",
  inputType = "text",
  disablePast = false,
  options,
  error,
  greaterThan,
  inputProps,
  asyncAutocompleteProps,
  priceFieldProps,
}: TGenericInput) {
  const { t } = useTranslation();
  const watchedGreaterThanValue = useWatch({ name: greaterThan || "" });

  const greaterThanValue = useMemo(() => {
    if (!greaterThan || !watchedGreaterThanValue) return null;
    if (typeof watchedGreaterThanValue === "string") {
      const parsed = parseISO(watchedGreaterThanValue);
      return isValid(parsed) ? parsed : null;
    }
    return watchedGreaterThanValue;
  }, [greaterThan, watchedGreaterThanValue]);

  if (type === "Autocomplete") {
    return (
      <Autocomplete
        disablePortal
        options={options || []}
        fullWidth
        disabled={!isEditAllowed}
        onChange={(_, value) => field.onChange(value)}
        value={field.value}
        isOptionEqualToValue={(option, value) => String(option.value) === String(value?.value)}
        renderInput={(params) => (
          <TextField
            {...params}
            {...field}
            label={label}
            variant={variant}
            error={Boolean(error)}
            helperText={error ? t(`common.form.errors.${error?.message}`) : ""}
            InputLabelProps={field.value?.length ? { shrink: true } : {}}
          />
        )}
      />
    );
  }

  if (type === "AsyncAutocomplete") {
    const asyncOpts = (options || []) as AsyncAutocompleteOption[];
    const {
      loading = false,
      noOptionsText,
      inputValue = "",
      onInputChange,
      getOptionValue = (o) => o.value,
      initialOption,
      selectedOption,
      onOptionSelect,
    } = asyncAutocompleteProps ?? {};
    const storedValue = field.value;
    const id = storedValue != null && storedValue !== "" ? Number(storedValue) : null;
    const fromOptions =
      id != null
        ? asyncOpts.find((o) => getOptionValue(o) === id || getOptionValue(o) === storedValue)
        : null;
    const fromSelected =
      id != null &&
      selectedOption &&
      (getOptionValue(selectedOption) === id || getOptionValue(selectedOption) === storedValue)
        ? selectedOption
        : null;
    const fromInitial =
      id != null &&
      initialOption &&
      (getOptionValue(initialOption) === id || getOptionValue(initialOption) === storedValue)
        ? initialOption
        : null;
    const resolvedValue = fromOptions ?? fromSelected ?? fromInitial ?? null;
    return (
      <Autocomplete
        disablePortal
        options={asyncOpts}
        fullWidth
        disabled={!isEditAllowed}
        value={resolvedValue}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={(_, value) => {
          onOptionSelect?.(value ?? null);
          field.onChange(value != null ? getOptionValue(value) : null);
        }}
        loading={loading}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, v) =>
          v != null && getOptionValue(option) === getOptionValue(v)
        }
        noOptionsText={noOptionsText}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant={variant}
            error={Boolean(error)}
            helperText={error ? t(`common.form.errors.${error?.message}`) : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  if (["TextField", "TextAreaField"].includes(type)) {
    return (
      <TextField
        {...field}
        inputRef={field.ref}
        sx={{ textTransform: "capitalize" }}
        error={Boolean(error)}
        helperText={error ? t(`common.form.errors.${error?.message}`) : ""}
        fullWidth
        variant={variant}
        label={label}
        InputLabelProps={field.value?.length ? { shrink: true } : {}}
        disabled={!isEditAllowed}
        type={inputType}
        multiline={type === "TextAreaField"}
        InputProps={{
          startAdornment: type === "PhoneField" ? "(+33) " : undefined,
          inputComponent: type === "PhoneField" ? (PhoneMask as any) : undefined,
          style: { minHeight: type === "TextAreaField" ? "100px" : "auto" },
          ...((inputProps as any) || {}),
        }}
      />
    );
  }

  if (type === "PhoneField") {
    return (
      <MuiTelInput
        {...field}
        inputRef={field.ref}
        sx={{ textTransform: "capitalize" }}
        label={label}
        error={Boolean(error)}
        defaultCountry="FR"
        disabled={!isEditAllowed}
        variant="standard"
        InputLabelProps={field.value?.length ? { shrink: true } : {}}
        helperText={error ? t(`common.form.errors.${error?.message}`) : ""}
        fullWidth
      />
    );
  }

  if (type === "DateTimeField") {
    const dateValue = field.value
      ? field.value instanceof Date
        ? field.value
        : parseISO(field.value)
      : null;
    return (
      <DesktopDateTimePicker
        {...field}
        inputRef={field.ref}
        value={dateValue}
        onChange={(date) => {
          if (date == null) {
            field.onChange(undefined);
            return;
          }

          if (!isValid(date)) {
            field.onChange(undefined);
            return;
          }

          field.onChange(date.toISOString());
        }}
        disablePast={disablePast}
        ampm={false}
        sx={{ width: "100%" }}
        label={label}
        views={["month", "day", "year", "hours", "minutes"]}
        slotProps={{
          textField: {
            error: Boolean(error),
            helperText: error ? t(`common.form.errors.${error?.message}`) : "",
            variant,
          },
        }}
        minDateTime={greaterThanValue}
        disabled={!isEditAllowed}
      />
    );
  }

  if (type === "DateField") {
    const dateValue = field.value
      ? field.value instanceof Date
        ? field.value
        : parseISO(field.value)
      : null;
    return (
      <DesktopDatePicker
        {...field}
        inputRef={field.ref}
        value={dateValue}
        onChange={(date) => {
          if (date == null) {
            field.onChange(undefined);
            return;
          }

          if (!isValid(date)) {
            field.onChange(undefined);
            return;
          }

          field.onChange(date.toISOString());
        }}
        disablePast={disablePast}
        sx={{ width: "100%" }}
        label={label}
        views={["month", "day", "year"]}
        slotProps={{
          textField: {
            error: Boolean(error),
            helperText: error ? t(`common.form.errors.${error?.message}`) : "",
            variant,
          },
        }}
        minDate={greaterThanValue}
        disabled={!isEditAllowed}
      />
    );
  }

  if (type === "SelectField") {
    return (
      <FormControl disabled={!isEditAllowed} fullWidth error={Boolean(error)}>
        <InputLabel id={name} sx={{ textTransform: "capitalize" }}>
          {label}
        </InputLabel>
        <Select {...field} labelId={name} label={label} inputRef={field.ref}>
          {options?.map((option) => (
            <MenuItem key={`${name}-${String(option.value)}`} value={option.value}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{error ? t(`common.form.errors.${error?.message}`) : ""}</FormHelperText>
      </FormControl>
    );
  }

  if (type === "PriceField") {
    const numValue =
      typeof field.value === "number" && Number.isFinite(field.value) ? field.value : 0;
    return (
      <PriceInput
        value={numValue}
        onChange={(v) => field.onChange(v)}
        disabled={!isEditAllowed}
        error={Boolean(error)}
        helperText={error ? t(`common.form.errors.${error?.message}`) : ""}
        variant={variant}
        fullWidth
        {...priceFieldProps}
      />
    );
  }

  if (type === "RadioField") {
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      // Convert "true"/"false" strings to actual booleans
      if (value === "true") {
        field.onChange(true);
      } else if (value === "false") {
        field.onChange(false);
      } else {
        field.onChange(value);
      }
    };

    return (
      <FormControl disabled={!isEditAllowed} error={Boolean(error)} fullWidth>
        <FormLabel id={name} sx={{ textTransform: "capitalize" }}>
          {label}
        </FormLabel>
        <RadioGroup
          row
          value={String(field.value)}
          onChange={handleRadioChange}
          aria-labelledby={name}
        >
          <Stack direction="row" width="80%" justifyContent="space-between">
            {options?.map((option) => (
              <FormControlLabel
                key={`${name}-${String(option.value)}`}
                value={String(option.value)}
                control={<Radio />}
                label={option.name}
                sx={{ textTransform: "capitalize" }}
              />
            ))}
          </Stack>
        </RadioGroup>
        <FormHelperText>{error ? t(`common.form.errors.${error?.message}`) : ""}</FormHelperText>
      </FormControl>
    );
  }

  return null;
}
type TAlbInput = Omit<TGenericInput, "field" | "error">;
function AlbInput({
  name,
  label,
  type,
  isEditAllowed,
  variant,
  inputType = "text",
  disablePast,
  options,
  greaterThan,
  inputProps,
  asyncAutocompleteProps,
  priceFieldProps,
}: TAlbInput) {
  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error } }) => (
        <GenericInput
          {...{
            field,
            inputType,
            name,
            label,
            type,
            isEditAllowed,
            variant,
            options,
            disablePast,
            error,
            greaterThan,
            inputProps,
            asyncAutocompleteProps,
            priceFieldProps,
          }}
        />
      )}
    />
  );
}

export default AlbInput;
